/**
 * InlineToggles Component
 * Yes/no filters rendered as chips that light up when on, sharing one cell of
 * the filter bar grid. Applies on every click so filters take effect live.
 */

import React from 'react';
import { Box, Chip } from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';

/** Marks the wrapper as a filter-bar cell so the bar gives it a column width. */
export const FILTER_CELL_CLASS = 'InlineFilterBar-cell';
/** Cell that outgrows its column instead of stacking its chips. */
export const FILTER_CELL_WIDE_CLASS = 'InlineFilterBar-cell-wide';

/** Off chips stay dimmed so that lighting one up reads as the click's effect. */
const OFF_OPACITY = 0.7;

export interface InlineToggleItem {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

export interface InlineTogglesProps {
  items: InlineToggleItem[];
  disabled?: boolean;
}

export const InlineToggles: React.FC<InlineTogglesProps> = ({ items, disabled = false }) => (
  <Box
    className={
      items.length > 1 ? `${FILTER_CELL_CLASS} ${FILTER_CELL_WIDE_CLASS}` : FILTER_CELL_CLASS
    }
    sx={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: 1 }}
  >
    {items.map((item) => (
      <Chip
        key={item.label}
        label={item.label}
        icon={item.checked ? <CheckIcon /> : undefined}
        color={item.checked ? 'primary' : 'default'}
        variant={item.checked ? 'filled' : 'outlined'}
        onClick={disabled ? undefined : () => item.onChange(!item.checked)}
        disabled={disabled}
        aria-pressed={item.checked}
        sx={{
          whiteSpace: 'nowrap',
          opacity: item.checked ? 1 : OFF_OPACITY,
          transition: 'opacity 150ms',
          '&:hover': { opacity: 1 },
        }}
      />
    ))}
  </Box>
);
